/**
 * Face detection for the family section, done on the device.
 *
 * The couple's photograph is decoded, measured and cut up here and then
 * dropped. Nothing but the crops they pick is ever uploaded, so the picture
 * of their family never sits in a bucket waiting to be needed.
 */
import { ortWasmPath } from '../runtime';
import { fetchFaceModel } from '../state/api';
import { detectFaces } from './yunet';
import { CROP_EDGE, CROP_MARGIN, CROP_QUALITY, CROP_TYPE } from '../../constants';

let ortPromise = null;
let modelPromise = null;

/**
 * onnxruntime, loaded the first time a photograph is read.
 *
 * Imported here rather than at the top: the shared package is one barrel,
 * so a static import put the runtime in the main bundle of every app,
 * including the ones that never open this form. A failed load is not
 * cached, so the next photograph tries again.
 */
const setup = () => {
  if (!ortPromise) {
    ortPromise = import('onnxruntime-web')
      .then((ort) => {
        const path = ortWasmPath();
        if (path) ort.env.wasm.wasmPaths = path;
        return ort;
      })
      .catch((err) => {
        ortPromise = null;
        throw err;
      });
  }
  return ortPromise;
};

/**
 * The detector's bytes, fetched once for the page.
 *
 * Through our own API rather than by handing onnxruntime the static URL:
 * static is served without cross origin headers, and onnxruntime fetches
 * without our auth header. A failed fetch is not cached, so the next
 * photograph tries again.
 */
const getModel = () => {
  if (!modelPromise) {
    modelPromise = fetchFaceModel().catch((err) => {
      modelPromise = null;
      throw err;
    });
  }
  return modelPromise;
};

const createCanvas = (width, height) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

/**
 * The picture as pixels this module can work on. `createImageBitmap` decodes
 * off the main thread where the browser has it, which every browser this
 * form supports does.
 */
export const readImage = (file) => createImageBitmap(file);

/** Let go of a decoded picture. A bitmap is pixels, and they are not small. */
export const releaseImage = (bitmap) => {
  if (bitmap && typeof bitmap.close === 'function') bitmap.close();
};

/** Every face on one decoded picture, as ratios of it. */
export const findFaces = async (bitmap) => {
  const ort = await setup();
  return detectFaces(bitmap, {
    ort,
    model: await getModel(),
    createCanvas,
  });
};

/**
 * One face cut out of the picture, as a JPEG blob ready to upload.
 *
 * The box is a ratio, so the pixels are worked out against the real size
 * here. The crop is squared off first: a face tag is shown in a square
 * frame, and squaring at the crop rather than in CSS means the stored file
 * is the thing that was actually looked at.
 */
const cutFace = (bitmap, box) => {
  const left = (box.Left || 0) * bitmap.width;
  const top = (box.Top || 0) * bitmap.height;
  const width = (box.Width || 0) * bitmap.width;
  const height = (box.Height || 0) * bitmap.height;
  const side = Math.max(width, height) * (1 + CROP_MARGIN);
  const cx = left + width / 2;
  const cy = top + height / 2;

  const canvas = createCanvas(CROP_EDGE, CROP_EDGE);
  const ctx = canvas.getContext('2d');
  // Anything the square reaches past the edge of the photograph is filled
  // rather than left transparent, because the crop is stored as a JPEG.
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, CROP_EDGE, CROP_EDGE);
  ctx.drawImage(bitmap, cx - side / 2, cy - side / 2, side, side,
    0, 0, CROP_EDGE, CROP_EDGE);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob)
        : reject(new Error('That face could not be cut out.'))),
      CROP_TYPE,
      CROP_QUALITY
    );
  });
};

/**
 * One face cut out of the photograph it was found on.
 *
 * The picture is decoded here and dropped again rather than kept between
 * crops. Decoded pixels are tens of megabytes for a photograph off a phone,
 * and holding one per photograph made adding the next one crawl. The file
 * itself is small enough to keep, so that is what the page holds.
 */
export const cropFace = async (file, box) => {
  const bitmap = await createImageBitmap(file);
  try {
    return await cutFace(bitmap, box);
  } finally {
    releaseImage(bitmap);
  }
};
